import { Fragment, useRef } from 'react';
import { MotionValue, m, useScroll, useTransform } from 'framer-motion';
// @mui
import { Box } from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';
//
import { STAGE, TYPE } from '../../../theme/stage';
import type { Accented } from '../copy';
import useStageMode from '../useStageMode';
import Rich from './Rich';

// ----------------------------------------------------------------------

type Props = {
  as?: 'h2';
  id: string;
  parts: Accented;
  sx?: SxProps<Theme>;
};

type WordProps = {
  p: MotionValue<number>;
  from: number;
  to: number;
  accent: boolean;
  children: string;
};

function wordsOf(parts: Accented) {
  const words: { word: string; accent: boolean; space: boolean }[] = [];
  let space = false;
  parts.forEach((part) => {
    const accent = typeof part !== 'string';
    const text = typeof part === 'string' ? part : part.i;
    text.split(/(\s+)/).forEach((chunk) => {
      if (/^\s*$/.test(chunk)) {
        space = space || chunk.length > 0;
        return;
      }
      words.push({ word: chunk, accent, space: words.length > 0 && space });
      space = false;
    });
  });
  return words;
}

function Word({ p, from, to, accent, children }: WordProps) {
  const opacity = useTransform(p, [from, to], [0, 1]);

  return (
    <Box
      component="span"
      className="w"
      sx={{ position: 'relative', display: 'inline-block', ...(accent && TYPE.accent) }}
    >
      <Box component="span" className="dim" sx={{ color: STAGE.mute }}>
        {children}
      </Box>
      <m.span
        aria-hidden
        className="lit"
        style={{
          position: 'absolute',
          left: 0,
          top: 0,
          color: STAGE.ink,
          userSelect: 'none',
          willChange: 'opacity',
          opacity,
        }}
      >
        {children}
      </m.span>
    </Box>
  );
}

export default function ReadAlong({ as = 'h2', id, parts, sx }: Props) {
  const ref = useRef<HTMLHeadingElement>(null);

  const { reduced } = useStageMode();

  const { scrollYProgress: p } = useScroll({ target: ref, offset: ['start 0.85', 'end 0.45'] });

  const words = wordsOf(parts);

  return (
    <Box
      ref={ref}
      component={as}
      id={id}
      sx={[{ color: STAGE.ink }, ...(Array.isArray(sx) ? sx : [sx])]}
    >
      {reduced ? (
        <Rich parts={parts} />
      ) : (
        words.map(({ word, accent, space }, i) => (
          <Fragment key={i}>
            {space && ' '}
            <Word p={p} from={i / words.length} to={(i + 1) / words.length} accent={accent}>
              {word}
            </Word>
          </Fragment>
        ))
      )}
    </Box>
  );
}
